import useGetCampaign from "hooks/useGetCampaign";
import useGetShareCount from "pages/campaign/hooks/useGetShareCount";
import React, { useEffect, useState } from "react";
import { IconClock } from "components/Icon";
import ImageCarousel from "components/ImageCarousel";
import CampaignHero from "components/CampaignHero";
import LinkButton from "components/LinkButton";
import Leaderboard from "components/Leaderboard";
import ProgressMeter from "components/ProgressMeter/ProgressMeter";
import { campaignEndTimeDisplay } from "helpers/campaignEndTimeDisplay";
import ShareModal from "pages/campaign/components/ShareModal";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import CampaignContent from "pages/campaign/components/CampaignContent";
import InfoBar from "pages/campaign/components/InfoBar";
import useGetCampaignCountdown from "hooks/useGetCampaignCountdown";
import useGetCampaignAssets from "./hooks/useGetCampaignAssets";
import useGetSupporters from "./hooks/useGetSupporters";
import CampaignSkeleton from "./components/Skeleton";

import "./style.scss";

const TeamPage = (props) => {
  const {
    team, campaignId, paymentUrl, fundraisersLeaderboard, campaignVideo, currencySymbol,
  } = props;

  const { loading: campaignDataLoading, campaign } = useGetCampaign(campaignId);
  const { loading: assetsLoading, assetData: images } = useGetCampaignAssets(campaignId);
  const {
    campaignEndsTime, isLessThanOneDay, setEndTime, getBrowserEndTimeString, hasEnded,
  } = useGetCampaignCountdown();
  const shareCount = useGetShareCount(campaignId);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const { translate: t } = useLanguageProvider();
  const hasMultipleImages = images && images.length >= 2;
  const showHero = !!((images && images.length <= 1) || (campaignVideo && images.length <= 1));
  const { name: teamName, supporters_count: supportersCount } = useGetSupporters(
    campaignId,
    team.name,
  );

  useEffect(() => {
    if (campaign) {
      setEndTime(campaign.duration.finishAtUtc);
    }
  }, [campaign, setEndTime]);

  const toggleModal = () => setShareModalOpen((isOpen) => !isOpen);
  const contentLoading = campaignDataLoading || assetsLoading || !team;

  if (!campaign || contentLoading) {
    return <CampaignSkeleton />;
  }

  const campaignIsActive = campaign.status === "Live" || campaign.status === "Approved" || campaign.status === "Infinity";
  const shouldAllowDonations = campaignIsActive && !(hasEnded && !campaign.duration.isInfinityMode);

  const campaignImage = images[0]?.cdn_url || images[0]?.url;
  const shareButton = (value) => (
    <LinkButton
      className="campaign-container__share-button"
      onClick={toggleModal}
      look="secondary-outline"
      isBold
      size="normal"
      data-test="campaign-share-button"
    >
      {value}
    </LinkButton>
  );

  const donateButton = (
    <LinkButton
      href={
        fundraisersLeaderboard && fundraisersLeaderboard.length > 0
          ? `${paymentUrl}?f=${fundraisersLeaderboard[0].id}`
          : `${paymentUrl}`
      }
      look="primary"
      isBold
      isDisabled={!shouldAllowDonations}
      size="normal"
      data-test="team-page-donate-button"
      className="campaign-container__donate-button"
    >
      {shouldAllowDonations
        ? campaign.matchGiving
          && campaign.matchGiving.enabled
          && !campaign.matchGiving.fulfilledGiving
          ? t("Double your donation")
          : t("Donate now")
        : t("Donations closed")}
    </LinkButton>
  );
  const campaignProgress = (
    <div className="campaign-container__animated-total">
      <div className="campaign-container__progress-bar">
        <ProgressMeter
          currency={campaign.currency}
          currencySymbol={currencySymbol}
          raised={team.raised || 0}
          target={team.target}
        />
      </div>
      {donateButton}

      {!hasEnded && campaignEndTimeDisplay(
        campaign.status,
        campaignEndsTime,
      ) && (
      <>
        <p className="campaign-container__countdown --notification">
          <span
            className={isLessThanOneDay ? "timer --closing-soon" : "timer"}
            data-test="campaign-countdown"
          >
            <IconClock height="1.75rem" className="clock-icon" />
            {" "}
            { t("Ends in") }
            {" "}
            {campaignEndsTime}
          </span>
        </p>

        <p className="campaign-container__end-date" data-test="campaign-countdown-date">
          {getBrowserEndTimeString(campaign.duration)}
        </p>
      </>
      )}
    </div>
  );

  return (
    <>
      {hasMultipleImages && <ImageCarousel images={images} video={campaignVideo} />}
      <div className="team-page-container">
        <div className="campaign-container">
          <div className="campaign-container__content">
            {showHero && (
              <CampaignHero
                imageAltText="Banner image for the campaign"
                imageHref={campaignImage}
                videoHref={campaignVideo}
                data-test="team-page-hero"
              />
            )}
            <section className="campaign-container__information">
              <div className="team-page-container__fundraiser-signup">
                <h1 className="campaign-container__title --page-title" data-test="team-page-header">
                  {teamName}
                </h1>
                <LinkButton
                  href={`/project/${campaignId}/signup`}
                  look="secondary-outline"
                  size="normal"
                  data-test="team-page-join-team-link"
                >
                  {t("Join Team")}
                </LinkButton>
              </div>
              <InfoBar type="fundraiser" fundraiserUserImage={team.logo} campaignId={campaignId} />
              <div
                className="campaign-container__sticky-donate --mobile"
                data-test="team-page-sticky-bar-mobile"
              >
                {campaignProgress}
              </div>

              {fundraisersLeaderboard && fundraisersLeaderboard.length > 0 && (
                <div data-test="team-page-fundraiser-leaderboard">
                  {fundraisersLeaderboard && fundraisersLeaderboard.length > 0 && (
                    <Leaderboard
                      data={fundraisersLeaderboard}
                      dataType="fundraisers"
                      heading={null}
                      viewMoreCopy={t("View more fundraisers")}
                      isLinkable
                      v2
                    />
                  )}
                </div>
              )}

              <LinkButton
                className="team-page-container__join-team --mobile"
                href={`/project/${campaignId}/signup`}
                look="secondary-outline"
                size="normal"
                data-test="team-page-join-team-link"
              >
                {t("Join Team")}
              </LinkButton>

              <div className="campaign-container__share-button-container --top campaign-container__separator">
                <div className="campaign-container__share-values-container">
                  {fundraisersLeaderboard && fundraisersLeaderboard.length > 0 && (
                    <div className="campaign-container__share-values">
                      <h3>{fundraisersLeaderboard.length}</h3>
                      <p className="campaign-container__share-values-subtitle --small">
                        {t("Fundraisers")}
                      </p>
                    </div>
                  )}
                  {supportersCount > 0 && (
                    <div
                      className="campaign-container__share-values"
                      data-test="team-page-supporters-total"
                    >
                      <h3>{supportersCount}</h3>
                      <p className="campaign-container__share-values-subtitle --small">
                        {supportersCount === 1 ? t("Supporter") : t("Supporters")}
                      </p>
                    </div>
                  )}

                  <div className="campaign-container__share-values">
                    <h3>{shareCount}</h3>
                    <p className="campaign-container__share-values-subtitle --small">{t("Shares")}</p>
                  </div>
                </div>
                {shareButton(t("Share"))}
              </div>

              <CampaignContent campaignId={campaignId} type="campaign" />

              <ShareModal
                campaignId={campaignId}
                isOpen={shareModalOpen}
                shareAction={toggleModal}
              />
            </section>
          </div>
          <div className="campaign-container__status-container --desktop">
            <div
              className="campaign-container__sticky-donate --desktop"
              data-test="team-page-sticky-bar"
            >
              <div className="well">{campaignProgress}</div>
            </div>
          </div>
        </div>
        <div className="campaign-container__sticky-bar-mobile --mobile">
          {shareButton(t("Share"))}
          {donateButton}
        </div>
      </div>
    </>
  );
};

export default TeamPage;
